import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useMatch} from '@reach/router'
import compose from 'lodash/flowRight'

import useThrottledEffect from 'hooks/useThrottledEffect'
import useSmartNavigate from 'hooks/useSmartNavigate'

import {Button, Form, FormGroup, Input, FormText, Alert, Label} from 'reactstrap'
import Recaptcha from 'react-recaptcha'
import Link from 'components/links/Link'
import OldLinkWithoutNavigation from 'components/links/OldLinkWithoutNavigation'

import {registerTalentId} from './data/baseMutations'

import {restApi} from 'utils/restApi'
import {parseBackendMessage} from 'utils/translationUtils'
import {changeLanguageTo} from './TopNav'

import {faIcon, coloredIcon} from 'utils/visualHelpers'

import logoIcon from 'icons/logo/logo.png'
import talentLogoIconRU from 'icons/logo/talent_logo_ru.png'
import talentLogoIconEN from 'icons/logo/talent_logo_en.png'
import {useCookies} from 'react-cookie'
import {EMAIL_REGEX} from 'constants/constants'


const loginManager = {
    resetApolloCache: (apolloClient) => {
        // Let's assume the user logs in.
        // First: do whatever is necessary to set the user's session.
        // Next: you absolutely must reset the store. This will clear the prior user's data from
        // memory and will cause all of the open queries to refetch using the new user's session.
        apolloClient.resetStore();
        // apolloClient.clearStore();

        // // Resume cache persistence. The cache storage provider will be empty right now,
        // // but it will written with the new user's data the next time the trigger fires.
        // this.props.apolloCachePersistor.resume();
    },

    logout: (apolloClient, navigate, uriMatch, t) => {
        // logout user
        console.log("Logging out...");
        restApi.getDataWithStatus('logout')
            .then(({status, data}) => {
                loginManager.resetApolloCache(apolloClient);
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`);
            }, ({status, error}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {error: error && (t('appBase.Authorization.logout.error') + error)}});
            });
    },

    login: (apolloClient, email, password, goBackLinkData, navigate, uriMatch, t) => {
        restApi.getDataWithStatus('login', {email: email, password: password})
            .then(({status, data}) => {
                loginManager.resetApolloCache(apolloClient);
                const returnUrl = (goBackLinkData && goBackLinkData.to) || `/${uriMatch.lang}/${uriMatch.region}/`;
                const returnState = (goBackLinkData && goBackLinkData.state) || {};
                navigate(returnUrl, {state: {returnState}});
            }, ({status, error}) => {
                // special case: we need to reset form on navigation to login, but here is navigation from login to login :(
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/relogin`, {state: {goBackLinkData,
                    error:
                        status === 404 ? t('appBase.Authorization.login.errors.error2') :
                            status === 401 ? t('appBase.Authorization.login.errors.error3') :
                                t('appBase.Authorization.login.errors.error1') + error},
                    goBackLinkData: goBackLinkData });
            });
    },

    setPassword: (apolloClient, password, resetToken, goBackLinkData, navigate, uriMatch, t) => {
        restApi.getDataWithStatus('set', {password: password, token: resetToken})
            .then(({status, data}) => {
                // user was logged in
                loginManager.resetApolloCache(apolloClient);
                const returnUrl = (goBackLinkData && goBackLinkData.to) || `/${uriMatch.lang}/${uriMatch.region}/`;
                const returnState = (goBackLinkData && goBackLinkData.state) || {};
                navigate(returnUrl, {state: {...returnState, info: t('appBase.Authorization.set.info')}});
            }, ({status, error}) => {
                let returnUrl;
                let returnState;
                const isAuthorized = goBackLinkData && goBackLinkData.isAuthorized;
                if (isAuthorized) {
                    // returning back
                    returnUrl = (goBackLinkData && goBackLinkData.to) || `/${uriMatch.lang}/${uriMatch.region}/`;
                    returnState = (goBackLinkData && goBackLinkData.state) || {};
                } else {
                    // keeping goBackLinkData until better times
                    returnUrl = `/${uriMatch.lang}/${uriMatch.region}/auth/login`;
                    returnState = {goBackLinkData};
                }
                navigate(returnUrl, {state: {...returnState, error:
                    status === 400 ? t('appBase.Authorization.set.errors.error2') :
                        t('appBase.Authorization.set.errors.error1') + error
                }});
            });
    },

    resetPassword: (email, goBackLinkData, navigate, uriMatch, t) => {
        // logout user
        restApi.getDataWithStatus('reset', {email: email, return_url: goBackLinkData && goBackLinkData.to})
            .then(({status, data}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, info: t('appBase.Authorization.reset.info')}});
            }, ({status, error}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error:
                    status === 404 ? t('appBase.Authorization.reset.errors.error2') :
                        t('appBase.Authorization.reset.errors.error1') + error}});
            });
    },

    registerUser: (apolloClient, email, name, surname, talentId, goBackLinkData, navigate, uriMatch, t) => {
        // logout user
        restApi.getDataWithStatus(
            'register',
            { email: email, name: name, surname: surname, talent_id: talentId, return_url: goBackLinkData && goBackLinkData.to })
            .then(({status, data}) => {
                let error = data['error'];
                let isUserExists = data['isUserExists'];
                let isActive = data['isActive'];
                if (error) {
                    navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error: error}});
                } else if (isUserExists && !talentId) {
                    navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData,
                        info: t('appBase.Authorization.register.infos.info1')}});
                } else {
                    navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData,
                        info: isUserExists && talentId ? t('appBase.Authorization.register.infos.info2') :
                            !isUserExists && talentId && isActive ? t('appBase.Authorization.register.infos.info3') :
                            t('appBase.Authorization.register.infos.info4')}});
                }
            }, ({status, error}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error:
                    status === 400 ? t('appBase.Authorization.register.errors.error2') :
                        t('appBase.Authorization.register.errors.error1') + error}});
            });
    },

    resetToken: (searchString, navigate, uriMatch, t) => {
        let token = '', returnUrl = null;
        searchString.split('&').forEach(param => {
            const [name, value] = param.split('=');
            if (name === 'token') {
                token = decodeURIComponent(value);
            }
            if (name === 'returnUrl') {
                returnUrl = decodeURIComponent(value);
            }
        });
        restApi.getDataWithStatus('check_reset_token', {token: token})
            .then(({status, data}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/set?${token}`, {state: {goBackLinkData: {to: returnUrl},
                    info: data['first_time'] ? t('appBase.Authorization.resetToken.infos.info1') : t('appBase.Authorization.resetToken.infos.info2'),
                    error: ''}});
            }, ({status, error}) => {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData: {to: returnUrl},
                    info: '',
                    error: status === 404 ? t('appBase.Authorization.resetToken.errors.error2') :
                        status === 408 ? t('appBase.Authorization.resetToken.errors.error3') :
                            t('appBase.Authorization.resetToken.errors.error1') + error}});
            });
    },

    initiateTalentOauth: (goBackLinkData, navigate, uriMatch, t) => {
        console.log('loginManager.initiateTalentOauth:');
        const returnUrl = (goBackLinkData && goBackLinkData.to) || `/${uriMatch.lang}/${uriMatch.region}/`;
        const returnState = (goBackLinkData && goBackLinkData.state) || {};
        const isAuthorized = goBackLinkData && goBackLinkData.isAuthorized;
        restApi.getDataWithStatus('get_talent_oauth_redirect', {url_lang: uriMatch.lang, return_url: returnUrl, is_authorized: isAuthorized})
            .then(({status, data}) => {
                console.log('       promise.ok');
                // navigate((data && data.url) || `/${uriMatch.lang}/${uriMatch.region}/`);
                window.location.replace((data && data.url) || `/${uriMatch.lang}/${uriMatch.region}/`);
            }, ({status, error}) => {
                console.log('       promise.error');
                navigate(returnUrl, {state: {...returnState, error: error && (t('appBase.Authorization.initiateTalentOauth.error') + error)}});
            });
    },

    completeTalentOauth: (searchString, navigate, apolloClient, uriMatch, t) => {
        console.log("completeTalentOauth...");
        let returnUrl = null, isAuthorized, error = null, talentData = {};
        searchString.split('&').forEach(param => {
            const [name, value] = param.split('=');
            if (name === 'returnUrl') {
                returnUrl = decodeURIComponent(value);
            }
            if (name === 'isAuthorized') {
                isAuthorized = (decodeURIComponent(value) || '').toLowerCase() === 'true';
            }
            if (name === 'error') {
                error = decodeURIComponent(value);
            }
            if (['id', 'email', 'firstName', 'lastName', 'userEmailWithTalentId', 'isTalentEmailExists', 'isAgreementDateExists'].includes(name)) {
                talentData[name] = decodeURIComponent(value);
                if (name === 'id' && talentData[name]) {
                    talentData[name] = Number(talentData[name])
                }
                if (['isTalentEmailExists', 'isAgreementDateExists'].includes(name) && talentData[name]) {
                    talentData[name] = talentData[name].toLowerCase() === 'true'
                }
            }
        });
        returnUrl = returnUrl || `/${uriMatch.lang}/${uriMatch.region}/`;
        const goBackLinkData = {to: returnUrl, isAuthorized: isAuthorized};
        error = error || (!talentData.id && t('appBase.Authorization.completeTalentOauth.errors.error2'));
        if (error) {
            // return back if error
            if (isAuthorized) {
                navigate(returnUrl, {state: {error: error}});
            } else {
                navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error: error}});
            }
        } else if (talentData.userEmailWithTalentId) {
            if (isAuthorized) {
                navigate(returnUrl, {state: {error: t('appBase.Authorization.completeTalentOauth.errors.error3')}});
            } else {
                // login if email found
                console.log("Logging in...");
                restApi.getDataWithStatus('login', {email: talentData.userEmailWithTalentId, talent_id: talentData.id})
                    .then(({status, data}) => {
                        const error = data['error'];
                        if (!error) {
                            loginManager.resetApolloCache(apolloClient);
                            navigate(returnUrl);
                        } else {
                            navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error: error}});
                        }
                    }, ({status, error}) => {
                        navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData, error: t('appBase.Authorization.completeTalentOauth.errors.error1') + error}});
                    });
            }
        } else {
            // register new account
            navigate(
                `/${uriMatch.lang}/${uriMatch.region}/auth/registerTalentId`,
                {state: {goBackLinkData, talentData: talentData}});
        }
    }
};


const Authorization = compose(
    registerTalentId
)((props) => {

    const {
        mode, apolloClient,
        registerTalentId} = props;
    const [,setCookie] = useCookies([]);
    const uriMatch = useMatch('/:lang/:region/*');
    const navigate = useSmartNavigate();
    const {t, i18n} = useTranslation();

    // special case navigation: relogin -> login
    if (mode === 'relogin') {
        navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: props.location.state});
    }

    const [formState, setFormState] = useState({
        email: localStorage.getItem('email') || "",
        name: localStorage.getItem('name') || "",
        surname: localStorage.getItem('surname') || "",
        agree: !!localStorage.getItem('agree') || false,
        agree2: !!localStorage.getItem('agree2') || false,
        captcha: props.location.hostname === "localhost",   // set if localhost
        password: "",
        password2: "",
        registerEmailChecks: {},
        loading: false,
        info: "",
        error: ""
    });

    const info =
        mode === 'logout' ? t('appBase.Authorization.logout.exit') :
        (['resetToken', 'initiateTalentOauth', 'completeTalentOauth'].includes(mode) || formState.loading) ? t('appBase.Authorization.logout.wait') :
        props.location.state && props.location.state.info ? props.location.state && props.location.state.info :
        formState.info ? formState.info : "";

    const error =
        mode === 'logout' ? "" :
        props.location.state && props.location.state.error ? props.location.state && props.location.state.error :
        formState.error;

    const goBackLinkData = props.location.state && props.location.state.goBackLinkData;
    const isAuthorized = goBackLinkData && goBackLinkData.isAuthorized;

    const {talentData} = props.location.state || {};

    let registerEmailValue = null;
    if (mode === 'registerTalentId' && !isAuthorized) {
        registerEmailValue = talentData.email;
    }

    const registerEmailChecksHasChanges =
        useThrottledEffect(() => {
            if (registerEmailValue) {
                restApi.getDataWithStatus('get_talent_registration_email_check', {email: registerEmailValue})
                    .then(({status, data}) => {
                        setFormState({
                            ...formState,
                            registerEmailChecks: {
                                isExists: data.isExists,
                                hasTalentId: data.hasTalentId
                            }
                        });
                    });
            }
        }, [registerEmailValue], 1000);


    // if we visit 'login' link then reset
    useEffect(() => {
        if (mode === 'login') {
            setFormState({...formState, loading: false});
        }
    }, [mode]);


    useEffect(() => {
        const locationSearch = props.location.search && props.location.search.substring(1);// location.search starts with '?'
        if (mode === 'logout') {
            loginManager[mode](apolloClient, navigate, uriMatch, t);
        } else if (mode === 'resetToken') {
            loginManager[mode](locationSearch, navigate, uriMatch, t);
        } else if (mode === 'initiateTalentOauth') {
            loginManager[mode](goBackLinkData, navigate, uriMatch, t);
        } else if (mode === 'completeTalentOauth') {
            loginManager[mode](locationSearch, navigate, apolloClient, uriMatch, t);
        }
        /*do not add uriMatch in the dependency array! */
    }, [mode, props.location.search, goBackLinkData, navigate, apolloClient, t]);


    const onChange = (e) => {
        switch (e.nativeEvent.target.name) {
            case "email":
            case "name":
            case "surname":
                localStorage.setItem(e.nativeEvent.target.name, e.nativeEvent.target.value);
                setFormState({...formState, [e.nativeEvent.target.name]: e.nativeEvent.target.value});
                break;
            case "password":
            case "password2":
                setFormState({...formState, [e.nativeEvent.target.name]: e.nativeEvent.target.value});
                break;
            case "agree":
            case "agree2":
                if (e.nativeEvent.target.checked) {
                    localStorage.setItem(e.nativeEvent.target.name, e.nativeEvent.target.checked);
                } else {
                    localStorage.removeItem(e.nativeEvent.target.name);
                }
                setFormState({...formState, [e.nativeEvent.target.name]: e.nativeEvent.target.checked});
                break;
            case "captcha":
                setFormState({...formState,
                    email: localStorage.getItem('email') || "",
                    name: localStorage.getItem('name') || "",
                    surname: localStorage.getItem('surname') || "",
                    agree: localStorage.getItem('agree') || false,
                    agree2: localStorage.getItem('agree2') || false,
                    captcha: true});
                break;
            default:
                throw new Error("Unknown field!");
        }
    };

    const onClick = (e) => {
        const {email, name, surname, agree, agree2, captcha, password, password2} = formState;
        e.stopPropagation();

        // clean registration data from local storage
        ['email', 'name', 'surname', 'agree', 'agree2']
            .forEach(e => localStorage.removeItem(e));

        if (mode === "register") {
            if (email && surname && name && agree && agree2 && captcha) {
                loginManager['registerUser'](apolloClient, email, name, surname, null, goBackLinkData, navigate, uriMatch, t);
                setFormState({...formState, loading: true, name: "", surname: ""});
            } else {
                setFormState({...formState, error: t('appBase.Authorization.register.errors.error3')})
            }
        }
        else if (mode === "set") {
            if (password === password2) {
                const resetToken = props.location.search && props.location.search.substring(1);    // location.search starts with '?'
                setFormState({...formState, loading: true, password: "", password2: "", error: ""});
                loginManager['setPassword'](apolloClient, password, resetToken, goBackLinkData, navigate, uriMatch, t);
            } else {
                setFormState({...formState, password: "", password2: "", error: t('appBase.Authorization.set.errors.error3')})
            }
        }
        else if (mode === "reset") {
            if (email) {
                loginManager['resetPassword'](email, goBackLinkData, navigate, uriMatch, t);
                setFormState({...formState, loading: true});
            } else {
                setFormState({...formState, error: t('appBase.Authorization.reset.errors.error3')})
            }
        }
        else if (mode === "login") {
            if (email && password) {
                loginManager['login'](apolloClient, email, password, goBackLinkData, navigate, uriMatch, t);
                setFormState({...formState, loading: true});
            } else {
                setFormState({...formState, error: t('appBase.Authorization.login.errors.error4')})
            }
        }
        else if (mode === "registerTalentId") {
            const talentId = talentData.id;
            if (isAuthorized) {
                // with graphqlQuery - user is logged in, graphql is accessible, and will refetch UserQuery
                registerTalentId(talentId)
                    .then(({data: {registerTalentId: {error}}}) => {
                        if (error) {
                            navigate(goBackLinkData.to, {state: {error: parseBackendMessage(error, t)}});
                        } else {
                            navigate(goBackLinkData.to, {state: {info: t('appBase.Authorization.registerTalentId.info')}});
                        }
                    });
                setFormState({...formState, loading: true});
            } else {
                const name = talentData.firstName;
                const surname = talentData.lastName;
                loginManager['registerUser'](apolloClient, registerEmailValue, name, surname, talentId, goBackLinkData, navigate, uriMatch, t);
                setFormState({...formState, loading: true, email: ""});
            }
        }
    };


    // rendering
    const {email, name, surname, agree, agree2, captcha, password, password2, registerEmailChecks, loading} = formState;

    // check is ready, email from Talent is avaible, no talentId associated with it in Orbita
    const isRegisterTalentEnabled =
        !registerEmailChecksHasChanges && registerEmailValue && !registerEmailChecks.hasTalentId;

    // new Orbita user or existing user without agreement
    const areCheckBoxesVisible = talentData &&
        (!talentData.isTalentEmailExists || !talentData.isAgreementDateExists);

    const areCheckBoxesVisibleButNotActive = areCheckBoxesVisible && (!agree || !agree2);

    const checkBoxesBlock = (
        <>
            <FormGroup>
                <FormGroup check>
                    <Label check>{' '}
                        <Input type="checkbox" name="agree" checked={agree}
                               onChange={onChange} disabled={loading}/>
                               {t('appBase.Authorization.register.giveAgreement')}
                        (<span className="controlelement"><a href="/personal.pdf" target="_blank">{t('appBase.Authorization.register.readAgreement2')}</a></span>)
                    </Label>
                </FormGroup>
            </FormGroup>
            <FormGroup>
                <FormGroup check>
                    <Label check>{' '}
                        <Input type="checkbox" name="agree2" checked={agree2}
                               onChange={onChange} disabled={loading}/>
                               {t('appBase.Authorization.register.giveAgreement2')}
                        (<span className="controlelement"><a href="/agreement.pdf" target="_blank">{t('appBase.Authorization.register.readAgreement2')}</a></span>)
                    </Label>
                </FormGroup>
            </FormGroup>
        </>);

    return (
        <div className="container pt-5 pb-5">
            <div className="row justify-content-center">
                <div className="col-9 col-sm-8 col-md-6 col-lg-5 col-xl-4 p-5 bg-light border border-secondary rounded">

                    {mode !== "registerTalentId" && /*TODO: сделать правильно */
                    <div className="text-center">
                        <Button className="m-1 p-1" color={i18n.resolvedLanguage === "ru" ? "primary" : "outline-secondary"}
                                disabled={i18n.resolvedLanguage === "ru"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    changeLanguageTo("ru", setCookie, apolloClient, i18n);
                        }}>RU</Button>

                        <Button className="m-1 p-1" color={i18n.resolvedLanguage === "en" ? "primary" : "outline-secondary"}
                                disabled={i18n.resolvedLanguage === "en"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    changeLanguageTo("en", setCookie, apolloClient, i18n);
                        }}>EN</Button>
                    </div>}

                    <div className="form-group">
                        <img className="img-fluid" src={logoIcon} alt="" />
                    </div>
                    {info &&
                    <Alert color="success">{info}</Alert>}
                    {error &&
                    <Alert color="danger">{error}</Alert>}

                    {mode === "register" &&
                    <Form>
                        <FormGroup>
                            <Input type="email" name="email" value={email} placeholder={t('appBase.Authorization.register.email')}
                                   onChange={onChange} disabled={loading}/>
                        </FormGroup>
                        <FormGroup>
                            <Input type="text" name="name" value={name} placeholder={t('appBase.Authorization.register.name')}
                                   onChange={onChange} disabled={loading}/>
                        </FormGroup>
                        <FormGroup>
                            <Input type="text" name="surname" value={surname} placeholder={t('appBase.Authorization.register.surname')}
                                   onChange={onChange} disabled={loading}/>
                        </FormGroup>
                        <FormGroup>
                            <FormGroup check>
                                <Label check>{' '}
                                    <Input type="checkbox" name="agree" checked={agree}
                                           onChange={onChange} disabled={loading}/>
                                           {t('appBase.Authorization.register.giveAgreement')}
                                    (<span className="controlelement"><a href="/personal.pdf" target="_blank">{t('appBase.Authorization.register.readAgreement2')}</a></span>)
                                </Label>
                            </FormGroup>
                        </FormGroup>
                        <FormGroup>
                            <FormGroup check>
                                <Label check>{' '}
                                    <Input type="checkbox" name="agree2" checked={agree2}
                                           onChange={onChange} disabled={loading}/>
                                           {t('appBase.Authorization.register.giveAgreement2')}
                                    (<span className="controlelement"><a href="/agreement.pdf" target="_blank">{t('appBase.Authorization.register.readAgreement2')}</a></span>)
                                </Label>
                            </FormGroup>
                        </FormGroup>
                        {!captcha &&
                        <FormGroup className="text-center">
                            <div style={{display: "inline-block"}}>
                                <Recaptcha
                                    sitekey="6LfrzcMUAAAAAMG9ILDeR6wtuTVRfrS9zU-o45uo"
                                    size="compact"
                                    render="explicit"
                                    onloadCallback={() => console.log('rendered')}
                                    verifyCallback={(e) => {
                                        onChange({nativeEvent: {target: {name: "captcha", value: true}}});
                                    }}
                                />
                            </div>
                        </FormGroup>}
                        <FormGroup className="text-center">
                            <Button color="primary"
                                    disabled={!EMAIL_REGEX.test(email) || !name.length || !surname.length || loading ||
                                    !agree || !agree2 || !captcha}
                                    onClick={onClick}>{t('appBase.Authorization.register.register')}</Button>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <FormText>
                                {t('appBase.Authorization.register.areRegistered')} <Link to={`/${uriMatch.lang}/${uriMatch.region}/auth/login`} state={{goBackLinkData}}>{t('appBase.Authorization.register.logIn')}</Link>
                            </FormText>
                        </FormGroup>
                    </Form>}

                    {mode === "login" &&
                    <Form>
                        <FormGroup>
                            <Input type="email" name="email" value={email} placeholder={t('appBase.Authorization.login.email')}
                                   onChange={onChange} disabled={loading}/>
                        </FormGroup>
                        <FormGroup>
                            <Input type="password" name="password" value={password} placeholder={t('appBase.Authorization.login.password')}
                                   onChange={onChange} disabled={loading}/>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <Button color="primary" disabled={!EMAIL_REGEX.test(email) || !password.length || loading}
                                    onClick={onClick}> {t('appBase.Authorization.login.logIn')} </Button>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <FormText>
                                {t('appBase.Authorization.login.forgetPassword')} <Link to={`/${uriMatch.lang}/${uriMatch.region}/auth/reset`} state={{goBackLinkData}}>{t('appBase.Authorization.login.resetPassword')}</Link>
                            </FormText>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <FormText>
                                {t('appBase.Authorization.login.notRegistered')} <Link to={`/${uriMatch.lang}/${uriMatch.region}/auth/register`} state={{goBackLinkData}}>{t('appBase.Authorization.login.register')}</Link>
                            </FormText>
                            <FormText>
                                 <Link to={goBackLinkData?.to || `/${uriMatch.lang}/${uriMatch.region}/events`}
                                       state={{goBackLinkData}}>
                                    {t('appBase.Authorization.login.logInWithoutRegistration')}
                                 </Link>
                            </FormText>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <span className="d-block text-primary">
                                {t('appBase.Authorization.login.talent')}
                            </span>
                            <Button className="mt-3" color="outline-secondary"
                                    onClick={() => navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/initiateTalentOauth`, {state: {goBackLinkData}})}>
                                {uriMatch.lang === 'ru' &&
                                <img className="img-fluid" src={talentLogoIconRU} alt="" style={{height: '30px'}}/>}
                                {uriMatch.lang !== 'ru' &&
                                <img className="img-fluid" src={talentLogoIconEN} alt="" style={{height: '30px'}}/>}
                            </Button>
                        </FormGroup>
                    </Form>}

                    {mode === "reset" &&
                    <Form>
                        <FormGroup>
                            <Input type="email" name="email" value={email} placeholder={t('appBase.Authorization.reset.email')}
                                   onChange={onChange} disabled={loading} />
                        </FormGroup>
                        <FormGroup className="text-center">
                            <Button color="primary" disabled={!EMAIL_REGEX.test(email) || loading} onClick={onClick}>
                                {t('appBase.Authorization.reset.sendToMail')}</Button>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <FormText>
                                <Link to={`/${uriMatch.lang}/${uriMatch.region}/auth/login`} state={{goBackLinkData}}>{t('appBase.Authorization.reset.goBack')}</Link>
                            </FormText>
                        </FormGroup>
                    </Form>}

                    {mode === "set" &&
                    <Form>
                        <FormGroup>
                            <Input type="password" name="password" value={password} placeholder={t('appBase.Authorization.set.password')}
                                   onChange={onChange}/>
                        </FormGroup>
                        <FormGroup>
                            <Input type="password" name="password2" value={password2}
                                   placeholder={t('appBase.Authorization.set.password2')} onChange={onChange}/>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <Button color="primary" disabled={!password.length || !password2.length}
                                    onClick={onClick}>
                                {isAuthorized ? t('appBase.Authorization.set.changePassword'): t('appBase.Authorization.set.setPassword')}
                            </Button>
                        </FormGroup>
                        <FormGroup className="text-center">
                            <FormText>
                                {isAuthorized &&
                                <Link to={goBackLinkData.to} state={goBackLinkData.state}>{t('appBase.Authorization.set.goBack')}</Link>}
                                {!isAuthorized &&
                                <OldLinkWithoutNavigation onClick={() => {
                                        setFormState({...formState, info: "", error: ""});
                                        navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData}});
                                    }}>{t('appBase.Authorization.set.cancel')}</OldLinkWithoutNavigation>}
                            </FormText>
                        </FormGroup>
                    </Form>}

                    {mode === 'registerTalentId' &&
                    <>
                    <Alert color="success">
                        <p>{t('appBase.Authorization.registerTalentId.talentAccount')}</p>
                        <p>{t('appBase.Authorization.registerTalentId.id')}<span className="fw-bold">{talentData.id}</span></p>
                        <p>{t('appBase.Authorization.registerTalentId.email')}<span className="fw-bold">{talentData.email}</span></p>
                        <p>{t('appBase.Authorization.registerTalentId.name')}<span className="fw-bold">{talentData.firstName} {talentData.lastName}</span></p>
                    </Alert>

                    <Form>
                        {isAuthorized &&  // add Talent from profile
                        <>
                        {areCheckBoxesVisible && checkBoxesBlock}

                        <FormGroup className="text-center pt-3">
                            <Button color="primary" onClick={onClick} disabled={loading || areCheckBoxesVisibleButNotActive}>
                                    {t('appBase.Authorization.registerTalentId.addTalent')}
                            </Button>
                        </FormGroup>

                        <FormGroup className="text-center">
                            <FormText>
                                <Link to={goBackLinkData.to} state={goBackLinkData.state}>
                                    {t('appBase.Authorization.registerTalentId.cancel')}
                                </Link>
                            </FormText>
                        </FormGroup>
                        </>}

                        {!isAuthorized &&   // register new Orbita account associated with Talent account
                        <>

                        {isRegisterTalentEnabled &&
                        <>
                            <FormGroup className="pt-2">
                                <Label check>
                                    {talentData.isTalentEmailExists &&
                                    <span>
                                        {t('appBase.Authorization.registerTalentId.areRegisteredInOrbita.part2')}
                                        <span className="fw-bold">{talentData.email}</span>.
                                        {t('appBase.Authorization.registerTalentId.areRegisteredInOrbita.part3')}
                                    </span>}
                                    {!talentData.isTalentEmailExists &&
                                    <span>
                                        {t('appBase.Authorization.registerTalentId.areRegisteredInOrbita.part4')}
                                        <span className="fw-bold">{talentData.email}</span>
                                    </span>}
                                </Label>
                            </FormGroup>

                            {areCheckBoxesVisible && checkBoxesBlock}
                        </>}

                        {registerEmailChecksHasChanges && registerEmailValue &&
                        <FormGroup className="pt-3">
                            <Label>
                                <span>
                                    {t('appBase.Authorization.registerTalentId.check')} {faIcon('fa-spinner', 'fa-pulse fa-fw')}
                                </span>
                            </Label>
                        </FormGroup>}

                       {!registerEmailChecksHasChanges && registerEmailValue && registerEmailChecks.hasTalentId &&
                        <FormGroup className="pt-3">
                            <Label>
                                <span className="text-danger">
                                    {t('appBase.Authorization.registerTalentId.errorEmailHasOtherTalentId')} {coloredIcon('blocked')}
                                </span>
                            </Label>
                       </FormGroup>}

                        {isRegisterTalentEnabled &&
                        <>

                        {talentData.isTalentEmailExists &&
                        <FormGroup>
                            <FormText tag="span" className="text-success">
                                <span>
                                    {t('appBase.Authorization.registerTalentId.registerEmailType.willBeAdded')}
                                </span>
                            </FormText>
                        </FormGroup>}

                        {!talentData.isTalentEmailExists &&
                        <FormGroup>
                            <FormText tag="span" className="text-success">
                                <span>
                                    {t('appBase.Authorization.registerTalentId.registerEmailType.willBeCreated')}
                                </span>
                            </FormText>
                        </FormGroup>}

                        <FormGroup className="text-center">
                            <Button color="primary" onClick={onClick}
                                    disabled={loading || areCheckBoxesVisibleButNotActive}>
                                    <span>
                                        {talentData.isTalentEmailExists ?
                                            t('appBase.Authorization.registerTalentId.add'):
                                            t('appBase.Authorization.registerTalentId.create')}
                                    </span>
                            </Button>
                        </FormGroup>
                        </>}

                        <FormGroup className="text-center">
                            <FormText>
                                <OldLinkWithoutNavigation onClick={() => {
                                    setFormState({...formState, email: "", info: "", error: ""});
                                    navigate(`/${uriMatch.lang}/${uriMatch.region}/auth/login`, {state: {goBackLinkData}});
                                }}>{t('appBase.Authorization.registerTalentId.cancel')}</OldLinkWithoutNavigation>
                            </FormText>
                        </FormGroup>
                        </>}
                    </Form>
                    </>}
                </div>
            </div>
        </div>);
});


export default Authorization;